import React, { useEffect, useState } from "react";
import Dialog from "../../../GenericComponents/dialog";
import { useFindWorkspaceMutation } from "../../../Redux/Home/HomeWorkspaceSlice";
import { HomeWorkspaceTiles, FindWorkspaceResponse } from '../../../Types/HomePageInterface';
import DOMPurify from 'dompurify';

import parse from "html-react-parser"; // safer than dangerouslySetInnerHTML
// import { getBaseUrl } from "../../../Utils";
import { useNavigate } from "react-router-dom";
import { onEnter } from "../../../GlobalMethods";
import { useApplyHighlight } from "../../useApplyHighlight";
import { FoundationsConstants } from "../../../FoundationsConstants";

interface FindWorkspaceDialogProps {
    isOpen: boolean;
    onClose: () => void;
    setShowLoading: (show: boolean) => void;
}

export const FindWorkspaceDialog: React.FC<FindWorkspaceDialogProps> = ({ isOpen, onClose, setShowLoading }) => {
    const [searchValue, setSearchValue] = useState("");
    const [findWorkspace, { data, isLoading, isError }] = useFindWorkspaceMutation();
    const [workspaces, setWorkspaces] = useState<HomeWorkspaceTiles[]>([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedWsid, setSelectedWsid] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<boolean | null>(null);
    const navigate = useNavigate();
    const {applyHighlight, highlightClass } = useApplyHighlight(FoundationsConstants.APPLY_HIGHLIGHT_DURATION);

    // Reset state when the dialog is closed
    const closeDialogOnCancelOrViewWorkspace = () => {
        setSearchValue("");
        setHasSearched(false);
        setWorkspaces([]);
        setSelectedWsid(null);
        setErrorMessage(false);
        // Close the dialog
        onClose();
    };
    const sanitize = (html: string | Node) => {
        return DOMPurify.sanitize(html, {
               ALLOW_UNKNOWN_PROTOCOLS: true, //This will allow any protocol in attributes even if it's unknown or custom.
        });
    }
    //first solution implemented when isOpen changes 
    // useEffect(()=>{
    //     setSearchValue('')
    //     setHasSearched(false)
    //     setWorkspaces([])
    // },[isOpen])

    useEffect(() => {
        if (!isOpen) {
            setShowLoading(false); // Set the loading state to false 
        }
    }, [isOpen, setShowLoading]);


    const handleFindClick = async () => {
        setSelectedWsid(null);
        setShowLoading(true);
        setErrorMessage(false);
        try {
            setHasSearched(true); // Mark that a search has been initiated

            // Call the API regardless of searchValue
            const response = await findWorkspace({
                searchCriteria: "Name",
                searchValue: searchValue.trim(),
            }).unwrap();
            setWorkspaces(response.data || []); // Handle case where data might be undefined
        } catch (error) {
            console.error("Error finding workspace:", error);
            setWorkspaces([]); // Reset workspaces on error
        }
         finally {
            // Ensure loading state is always cleared, even if there's an error.
            if (isOpen) {
                setShowLoading(false);
            }
        }

    };

    return (
        <Dialog
            isOpen={isOpen}
            title="Find / View Workspace"
            onClose={closeDialogOnCancelOrViewWorkspace}
            buttons={[
                {
                    label: "Cancel",
                    onClick: closeDialogOnCancelOrViewWorkspace,
                    className: "ui-button ui-corner-all ui-widget app-button negative",
                },
                {
                    label: "View Workspace",
                    onClick: () => {
                        if (selectedWsid === null) {
                            setErrorMessage(true)
                            applyHighlight("valmsg_user_dashboard_list", "applyHighLight", { autoHide: false });
                        }
                        else {
                            // Add API mutation or action for viewing workspace
                            navigate(`${location.pathname}${location.search}#workspace/${selectedWsid}`, { replace: true });
                            closeDialogOnCancelOrViewWorkspace()
                        }
                    },
                    className: "ui-button ui-corner-all ui-widget app-button",
                },
            ]}
            width={"518px"}
            height={"auto"}
        >
            <div className="searchlist-popup-header">
                <input
                    type="text"
                    id="user_serversearch_filter"
                    maxLength={100}
                    size={40}
                    placeholder="enter a few letters and click find"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => onEnter(e, handleFindClick)}

                />
                <span>&nbsp;&nbsp;</span>
                <button
                    type="button"
                    id="user_serversearch_button"
                    className="ui-button app-button"
                    onClick={handleFindClick}
                    disabled={isLoading}
                >
                    <span className="ui-button-text">Find</span>
                </button>
            </div>
            <span className="clear-both" />
            <span>&nbsp;&nbsp;</span>
            <ol id="user_dashboard_list" className="dashboard-list">
                {isLoading ?   (
                    <div className="loading-message"></div>
                ) : 
                isError ? (
                    <li className="error-message">Error fetching workspaces.</li>
                ) : hasSearched && workspaces.length === 0 ? ( // Show message only after a search
                    <li className="error-message">Sorry, no matches found. Try a different search.</li>
                ) : (
                    workspaces.map((workspace) => (
                        <li
                            key={workspace.wsid}
                            id={`dashboard_${workspace.wsid}`}
                            className={`${selectedWsid===workspace.wsid ? 'ui-selected' : 'ui-not-selected'}`}
                            onClick={() => {setSelectedWsid(workspace.wsid);setErrorMessage(false);}}
                            data-wsid={workspace.wsid}
                        >
                            <div className="float-left fieldradio">
                                <input
                                    type="radio"
                                    id={`dashboard_radio_${workspace.wsid}`}
                                    name="dashboard_radio"
                                    value={workspace.wsid}
                                    onChange={() => setSelectedWsid(workspace.wsid)}
                                    checked={selectedWsid === workspace.wsid}
                                />
                            </div>
                            <div className="float-left fieldcontent">
                                <div className="dashboard-list-header">{parse(sanitize(workspace.Name))}</div>
                                <div className="dashboard-list-sub-header">
                                {parse(sanitize(workspace.Description))}
                                </div>
                            </div>
                            <div className="clear-both"></div>
                        </li>
                    ))
                )}
            </ol>
            {errorMessage && (
                <div
                    id="valmsg_user_dashboard_list"
                    className={`padding-top-small field-validation-error ${highlightClass?.valmsg_user_dashboard_list || ''}`}
                >
                    Please select a workspace and try again.
                </div>
            )}
            <span
                id="user_dashboard_loading"
                className="hide ajax-loading-box popup-loading"
            >
                <span className="ajax-loading-image">Loading...</span>
            </span>
        </Dialog>

    )
}